/* <ugc-reels> — Karussell mit Dreh-Animation:
   3 feste Positionen (links / Mitte vorn / rechts), alle weiteren Clips
   unsichtbar außerhalb. Weiter/Wischen dreht: rechts -> Mitte, Mitte -> links,
   das Neue gleitet von außen herein (Endlos in beide Richtungen).
   Nur das mittlere Video spielt und startet bei jedem Wechsel von vorn. */
if (!customElements.get('ugc-reels')) {
  class UgcReels extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.track = this.querySelector('[data-ugc-track]');
      if (!this.track) return;
      this.initialized = true;

      this.items = Array.from(this.track.children);
      this.videos = Array.from(this.querySelectorAll('[data-ugc-video]'));
      this.videos.forEach((video) => { video.muted = true; });
      this.current = 0;
      this.prevX = new Map();

      /* Sichtbarkeit der Section steuert Start/Stopp */
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (this.inView) {
              this.playCenter(false);
            } else {
              this.pauseAll();
            }
          });
        },
        { threshold: 0.3 }
      );
      this.observer.observe(this);

      /* Klick: Seiten-Karte -> dorthin drehen, Mitte -> Ton an/aus */
      this.items.forEach((item, i) => {
        item.addEventListener('click', () => {
          if (i === this.current) {
            this.toggleSound(item.querySelector('[data-ugc-video]'));
          } else {
            const n = this.items.length;
            let d = (((i - this.current) % n) + n) % n;
            if (d > n / 2) d -= n;
            this.rotate(d > 0 ? 1 : -1);
          }
        });
      });

      const prev = this.querySelector('[data-ugc-prev]');
      const next = this.querySelector('[data-ugc-next]');
      if (prev) prev.addEventListener('click', () => this.rotate(-1));
      if (next) next.addEventListener('click', () => this.rotate(1));

      /* Wischen (Pointer Events) */
      this.track.addEventListener('pointerdown', (event) => {
        this.dragX = event.clientX;
      });
      this.track.addEventListener('pointerup', (event) => {
        if (this.dragX === undefined) return;
        const delta = event.clientX - this.dragX;
        this.dragX = undefined;
        if (Math.abs(delta) > 40) this.rotate(delta < 0 ? 1 : -1);
      });

      /* Breakpoint-Wechsel: Seiten-Abstand neu anwenden */
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.applyPositions(), 150);
      }, { passive: true });

      this.applyPositions();
      this.playCenter(true);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    rotate(direction) {
      const n = this.items.length;
      if (n < 2) return;
      this.current = (this.current + direction + n) % n;
      this.applyPositions();
      this.playCenter(true);
    }

    /* Positionen: 0 = Mitte (vorn), ±1 = Seiten (kleiner, hinten), Rest außen unsichtbar.
       Wechselt eine Karte die Seite (Wrap), wird sie erst ohne Animation auf die
       Eintrittsseite gesetzt und gleitet dann herein — keine Fahrt durch die Mitte. */
    applyPositions() {
      const n = this.items.length;
      /* Seiten-Abstand aus CSS-Variable (mobil 95, Desktop 76 = Überlappung) */
      const sideX = parseFloat(getComputedStyle(this.track).getPropertyValue('--ugc-side-x')) || 95;
      const hiddenX = sideX * 2.1;
      this.items.forEach((item, i) => {
        let d = (((i - this.current) % n) + n) % n;
        if (d > n / 2) d -= n;
        let x;
        let scale;
        let opacity;
        let z;
        if (d === 0) { x = 0; scale = 1; opacity = 1; z = 3; }
        else if (d === 1) { x = sideX; scale = 0.84; opacity = 0.6; z = 2; }
        else if (d === -1) { x = -sideX; scale = 0.84; opacity = 0.6; z = 2; }
        else { x = d > 0 ? hiddenX : -hiddenX; scale = 0.7; opacity = 0; z = 1; }

        const previousX = this.prevX.get(item);
        const sideFlip = previousX !== undefined && previousX !== 0 && x !== 0 && Math.sign(previousX) !== Math.sign(x);
        if (sideFlip) {
          item.classList.add('ugc--no-anim');
          item.style.transform = `translateX(${x > 0 ? hiddenX : -hiddenX}%) scale(0.7)`;
          item.style.opacity = '0';
          void item.offsetHeight; /* Reflow erzwingen */
          item.classList.remove('ugc--no-anim');
        }
        item.classList.toggle('ugc--side', d !== 0);
        item.style.transform = `translateX(${x}%) scale(${scale})`;
        item.style.opacity = String(opacity);
        item.style.zIndex = String(z);
        this.prevX.set(item, x);
      });
    }

    pauseAll() {
      this.videos.forEach((video) => {
        video.pause();
        video.muted = true;
        this.updateSoundIcon(video);
      });
    }

    /* Nur die Mitte spielt; restart = true startet den Clip von vorn */
    playCenter(restart) {
      const centerVideo = this.items[this.current]?.querySelector('[data-ugc-video]');
      this.videos.forEach((video) => {
        if (video !== centerVideo) {
          video.pause();
          video.muted = true;
          this.updateSoundIcon(video);
        }
      });
      if (centerVideo && this.inView !== false) {
        if (restart) centerVideo.currentTime = 0;
        centerVideo.play().catch(() => {});
      }
    }

    toggleSound(video) {
      if (!video) return;
      const wasMuted = video.muted;
      this.videos.forEach((v) => {
        v.muted = true;
        this.updateSoundIcon(v);
      });
      if (wasMuted) {
        video.muted = false;
        video.play().catch(() => {});
        this.updateSoundIcon(video);
      }
    }

    updateSoundIcon(video) {
      const btn = video.closest('[data-ugc-card]')?.querySelector('[data-ugc-sound]');
      if (!btn) return;
      btn.innerHTML = video.muted
        ? '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6h2.5L8 3v10L4.5 10H2a1 1 0 01-1-1V7a1 1 0 011-1z" fill="currentColor"/><path d="M11 6l4 4M15 6l-4 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>'
        : '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6h2.5L8 3v10L4.5 10H2a1 1 0 01-1-1V7a1 1 0 011-1z" fill="currentColor"/><path d="M11 5.5s1.5 1 1.5 2.5S11 10.5 11 10.5M13 4s2 1.5 2 4-2 4-2 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" fill="none"/></svg>';
      btn.setAttribute('aria-label', video.muted ? 'Ton an' : 'Ton aus');
    }
  }
  customElements.define('ugc-reels', UgcReels);
}
